import Storage from 'utilities/storage';
import { recordIdentityEvent } from 'utilities/uTagging';
import {
    BROWSER_MAP,
    getBrowserName,
    getBrowserVersion,
    isInvalidUserAgent,
    isValidBrowserForIos,
} from 'utilities/browser';
import { formatText, getLanguage } from 'core/i18n'; // @constants
import {
    IDENTITY_CONST,
    IDENTITY_VIDEOS_RECORD_TAG,
    PATH_IDENTITY_VIDEOCALL_BROWSER_ERROR,
    PATH_IDENTITY_VIDEOCALL_EVENT,
    PATH_IDENTITY_VIDEOSELFIE_BROWSER_ERROR,
    PATH_IDENTITY_VIDEOSELFIE_EVENT,
} from 'constants/uTagConstants/paths';
import { ROBO_VIDEOCALL, ROBO_VIDEOSELFIE, TC_VIDEOCALL, TC_VIDEOSELFIE } from 'constants/featureFlagging';
import { VIDEO_CALL, VIDEO_SELFIE } from 'constants/identityMethod';
import { EVENT_ONBOARDING } from 'constants/uTagConstants/RAConstants';
import {
    COUNTRY_CODE_DE,
    COUNTRY_CODE_EN,
    COUNTRY_CODE_ES,
    COUNTRY_CODE_NL,
    COUNTRY_CODE_PT,
} from 'constants/multiLanguage';
import { getMonth } from 'utilities/dateHelper/dateHelper';

let isRecordedVideoTag = false;

const setIFrameHeightBasedOnVideoType = type => {
    return type === VIDEO_SELFIE ? '655' : '400';
};

const setIFrameInstructionsBasedOnVideoType = type => {
    return type !== VIDEO_SELFIE ? 'col-xs-12 col-sm-9' : 'col-xs-12 col-sm-12';
};

const isTimeAvailableForVideoContent = ({ options, videoSelected }) => {
    const currentDate = new Date(options?.get('serverDate'));
    const currentHour = currentDate.getHours();
    const dayOfWeeK = currentDate.getDay();
    const isWeekDay = dayOfWeeK >= 1 && dayOfWeeK <= 5; // getDay returns between 0 and 6, starting from sunday
    const currentCountry = __ORIGIN_COUNTRY__.toLowerCase();

    const between10amTo10pm = currentHour >= 10 && currentHour < 22;
    const between9amTo9pm = currentHour >= 9 && currentHour < 21;
    const between11amTo11pm = currentHour >= 11 && currentHour < 23;

    const BASIC_VIDEO_TYPES = {
        [TC_VIDEOCALL]: VIDEO_CALL,
        [VIDEO_CALL]: VIDEO_CALL,
        [ROBO_VIDEOCALL]: VIDEO_CALL,
        [TC_VIDEOSELFIE]: VIDEO_SELFIE,
        [VIDEO_SELFIE]: VIDEO_SELFIE,
        [ROBO_VIDEOSELFIE]: VIDEO_SELFIE,
    };

    const typeOfVideoBasic = BASIC_VIDEO_TYPES[videoSelected];

    const TIME_AVAILABLE_FOR_VIDEOS_BY_COUNTRY = {
        [COUNTRY_CODE_ES]: typeOfVideo => {
            if (typeOfVideo === VIDEO_CALL) {
                return between10amTo10pm && isWeekDay;
            }

            return true;
        },
        [COUNTRY_CODE_DE]: typeOfVideo => {
            if (typeOfVideo === VIDEO_CALL) {
                return between9amTo9pm && isWeekDay;
            }

            return true;
        },
        [COUNTRY_CODE_NL]: typeOfVideo => {
            if (typeOfVideo === VIDEO_CALL) {
                return between10amTo10pm && isWeekDay;
            }

            return true;
        },
        [COUNTRY_CODE_PT]: typeOfVideo => {
            if (typeOfVideo === VIDEO_CALL) {
                return between11amTo11pm && isWeekDay;
            }

            return true;
        },
    };

    return TIME_AVAILABLE_FOR_VIDEOS_BY_COUNTRY[currentCountry](typeOfVideoBasic);
};

const showEnglishMessage = () => {
    return getLanguage() === COUNTRY_CODE_EN;
};

const recordInvalidBrowserForVideo = videoSelected => {
    if (!isRecordedVideoTag) {
        setTimeout(() => {
            isRecordedVideoTag = true;
            recordIdentityEvent(
                'onboarding',
                videoSelected === VIDEO_CALL ? PATH_IDENTITY_VIDEOCALL_EVENT : PATH_IDENTITY_VIDEOSELFIE_EVENT,
                'error/navegador',
                videoSelected === VIDEO_CALL
                    ? PATH_IDENTITY_VIDEOCALL_BROWSER_ERROR
                    : PATH_IDENTITY_VIDEOSELFIE_BROWSER_ERROR,
                `navegador no compatible con ${videoSelected === VIDEO_CALL ? 'videollamada' : 'videoselfie'}`
            );
        }, 500);
    }
};

const isValidBrowser = (isIosChromeActivated, videoSelected) => {
    const validBrowsers = [
        { name: BROWSER_MAP.chrome, minVersion: 23 },
        { name: BROWSER_MAP.edge, minVersion: 18.16 },
        { name: BROWSER_MAP.firefox, minVersion: 22 },
        { name: BROWSER_MAP.safari, minVersion: 11 },
        { name: BROWSER_MAP.opera, minVersion: 18 },
        { name: BROWSER_MAP.ios, minVersion: 11 },
        { name: BROWSER_MAP.samsung, minVersion: 10 },
    ];

    const browser = validBrowsers.filter(
        browser => browser.name === getBrowserName() && browser.minVersion <= parseFloat(getBrowserVersion())
    );

    let valid = false;
    const navUserAgent = navigator.userAgent.toUpperCase();

    browser.length > 0 && isValidBrowserForIos(isIosChromeActivated) && !isInvalidUserAgent(navUserAgent)
        ? (valid = true)
        : videoSelected && recordInvalidBrowserForVideo(videoSelected);

    return valid;
};

const checkCookie = options => {
    if (Storage.cookies.get('hour')) {
        const hour = Storage.cookies.get('hour');

        if (parseInt(hour, 10) >= 10 && parseInt(hour, 10) < 22) {
            return true;
        }

        return false;
    }

    return isTimeAvailableForVideoContent(options);
};

const getMonthName = value => {
    const months = [
        formatText('fullMonths-january'),
        formatText('fullMonths-february'),
        formatText('fullMonths-march'),
        formatText('fullMonths-april'),
        formatText('fullMonths-may'),
        formatText('fullMonths-june'),
        formatText('fullMonths-july'),
        formatText('fullMonths-august'),
        formatText('fullMonths-september'),
        formatText('fullMonths-october'),
        formatText('fullMonths-november'),
        formatText('fullMonths-december'),
    ];

    return months[value];
};

const getCurrentDate = options => {
    const currentDate = new Date(options.get('serverDate'));
    const currentday = currentDate.getDate();
    const currentMonth = getMonth(currentDate);
    const currentYear = currentDate.getFullYear();
    const currentHour = currentDate.getHours() % 12 || 12;
    const currentminutes = String(currentDate.getMinutes()).padStart(2, '0');
    const monthName = getMonthName(currentMonth - 1);

    return {
        currentDate,
        currentday,
        currentMonth,
        currentYear,
        currentHour,
        currentminutes,
        monthName,
    };
};

const isVideoCallAvailable = ({ options, videoSelected }) => isTimeAvailableForVideoContent({ options, videoSelected });

const checkVideoPermission = async ({ grantedPermission, changeFunction, videoSelected }) => {
    if (grantedPermission !== undefined) return;
    changeFunction('userProfile.requestingPermissions', true);

    try {
        const CAMERA_CONSTRAINS = { video: true, audio: true };
        const stream = await window.navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINS);
        stream.getTracks().forEach(x => {
            x.stop();
        });

        changeFunction('userProfile.grantedPermission', true);
    } catch (error) {
        const categoriaError = 'error/navegador-permisos-acceso-camara';
        recordIdentityEvent(
            EVENT_ONBOARDING,
            `${IDENTITY_CONST}/${IDENTITY_VIDEOS_RECORD_TAG[videoSelected]}/`,
            categoriaError,
            `${IDENTITY_CONST}/${IDENTITY_VIDEOS_RECORD_TAG[videoSelected]}/${categoriaError}`,
            'navegador permisos de acceso a camara'
        );

        changeFunction('userProfile.grantedPermission', false);
    }

    changeFunction('userProfile.requestingPermissions', false);
};

const getPermissionMessage = () => {
    const messageByBrowsers = [
        {
            name: 'Chrome',
            message: 'identity-videoCallAllowCameraMobileChrome',
        },
        {
            name: 'Safari',
            message: 'identity-videoCallAllowCameraMobileSafari',
        },
    ];
    const browserName = getBrowserName();
    const messageBrowser = messageByBrowsers.find(browser => browser.name === browserName);

    return messageBrowser && Object.keys(messageBrowser).length > 0
        ? messageBrowser.message
        : 'identity-videoCallAllowCameraMobile';
};

export default {
    isTimeAvailableForVideoContent,
    showEnglishMessage,
    isValidBrowser,
    checkCookie,
    getMonthName,
    getCurrentDate,
    setIFrameHeightBasedOnVideoType,
    setIFrameInstructionsBasedOnVideoType,
    isVideoCallAvailable,
    checkVideoPermission,
    getPermissionMessage,
};
